import { useEffect, useState } from 'react';
import api from '@/api/engine';
import type { TaskItem, TaskPriority, TaskStatus, TaskSummary } from '@/api/types';

const PRIORITY_COLOR: Record<TaskPriority, string> = {
  critical: 'text-red-400',
  high: 'text-orange-400',
  medium: 'text-yellow-400',
  low: 'text-gray-500',
};

const STATUS_COLOR: Record<TaskStatus, string> = {
  backlog: 'text-gray-500',
  todo: 'text-gray-400',
  in_progress: 'text-[#39FF14]',
  blocked: 'text-red-400',
  review: 'text-blue-400',
  done: 'text-gray-600',
  cancelled: 'text-gray-600',
};

/**
 * One read's honest state (Command Center Phase 1, packet C).
 *
 * A rejected read is never an empty list: `unavailable` keeps the error next
 * to the block so the operator sees that the block is missing, not empty.
 */
type ReadState<T> =
  | { kind: 'loading' }
  | { kind: 'live'; value: T }
  | { kind: 'unavailable'; message: string };

function readState<T>(
  result: PromiseSettledResult<T>,
  fallbackMessage: string,
  transform?: (value: T) => T,
): ReadState<T> {
  if (result.status === 'fulfilled') {
    return { kind: 'live', value: transform ? transform(result.value) : result.value };
  }
  return {
    kind: 'unavailable',
    message: result.reason instanceof Error ? result.reason.message : fallbackMessage,
  };
}

function StatBox({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <div className="bg-gray-900/40 border border-gray-800 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color: color ?? '#e5e7eb' }}>
        {value}
      </div>
    </div>
  );
}

function UnavailableNote({ label, message }: { label: string; message: string }) {
  return (
    <div className="text-xs">
      <p className="text-red-400">{label} unavailable</p>
      <p className="text-gray-500 mt-1">{message}</p>
    </div>
  );
}

function TaskRow({ task }: { task: TaskItem }) {
  return (
    <div className="flex items-start gap-3 py-2 border-t border-gray-800 text-xs">
      <span className={`${STATUS_COLOR[task.status]} uppercase tracking-wider w-[72px] text-[10px] mt-0.5 flex-shrink-0`}>
        {task.status.replace('_', ' ')}
      </span>
      <span className={`${PRIORITY_COLOR[task.priority]} uppercase tracking-wider w-[56px] text-[10px] mt-0.5 flex-shrink-0`}>
        {task.priority}
      </span>
      <div className="flex-1 min-w-0">
        <a
          href={`/board?task=${encodeURIComponent(task.id)}`}
          className="text-gray-200 hover:text-[#39FF14] transition-colors block truncate"
          title={task.title}
        >
          {task.title}
        </a>
        {(task.assigned_to || task.category) && (
          <div className="text-[10px] text-gray-500 mt-0.5">
            {task.assigned_to && <span>{task.assigned_to}</span>}
            {task.assigned_to && task.category && <span className="mx-1">·</span>}
            {task.category && <span>{task.category}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

export function TasksTab() {
  const [summary, setSummary] = useState<ReadState<TaskSummary>>({ kind: 'loading' });
  const [active, setActive] = useState<ReadState<TaskItem[]>>({ kind: 'loading' });
  const [blocked, setBlocked] = useState<ReadState<TaskItem[]>>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      api.tasks.summary(),
      api.tasks.list({ status: 'in_progress', limit: 50 }),
      api.tasks.list({ status: 'blocked', limit: 50 }),
    ]).then(([summaryResult, activeResult, blockedResult]) => {
      if (cancelled) return;
      setSummary(readState(summaryResult, 'Failed to load the task summary'));
      setActive(readState(activeResult, 'Failed to load active tasks', sortTasks));
      setBlocked(readState(blockedResult, 'Failed to load blocked tasks', sortTasks));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="p-6 font-mono text-sm text-gray-200 space-y-5">
      <div className="flex items-baseline justify-between">
        <div className="text-xs uppercase tracking-wider text-gray-500">Tasks overview</div>
        <a href="/board" className="text-xs text-[#39FF14] hover:underline">
          Open full board →
        </a>
      </div>

      {summary.kind === 'live' ? (
        <div className="grid grid-cols-5 gap-3">
          <StatBox label="Total" value={summary.value.total} />
          <StatBox label="Active" value={summary.value.by_status?.in_progress ?? 0} color="#39FF14" />
          <StatBox label="Blocked" value={summary.value.by_status?.blocked ?? 0} color="#f87171" />
          <StatBox label="Review" value={summary.value.by_status?.review ?? 0} color="#60a5fa" />
          <StatBox label="Overdue" value={summary.value.overdue_count} color="#fbbf24" />
        </div>
      ) : summary.kind === 'unavailable' ? (
        <UnavailableNote label="Task summary" message={summary.message} />
      ) : (
        <p className="text-gray-500">Loading task summary…</p>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-900/40 border border-gray-800 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">
            In progress {active.kind === 'live' && `(${active.value.length})`}
          </div>
          {active.kind === 'loading' ? (
            <p className="text-gray-500 text-xs">Loading…</p>
          ) : active.kind === 'unavailable' ? (
            <UnavailableNote label="Active tasks" message={active.message} />
          ) : active.value.length === 0 ? (
            <p className="text-gray-500 text-xs">No active tasks.</p>
          ) : (
            <div className="max-h-[calc(100vh-340px)] overflow-auto">
              {active.value.map((t) => (
                <TaskRow key={t.id} task={t} />
              ))}
            </div>
          )}
        </div>

        <div className="bg-gray-900/40 border border-gray-800 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">
            Blocked {blocked.kind === 'live' && `(${blocked.value.length})`}
          </div>
          {blocked.kind === 'loading' ? (
            <p className="text-gray-500 text-xs">Loading…</p>
          ) : blocked.kind === 'unavailable' ? (
            <UnavailableNote label="Blocked tasks" message={blocked.message} />
          ) : blocked.value.length === 0 ? (
            <p className="text-gray-500 text-xs">No blocked tasks.</p>
          ) : (
            <div className="max-h-[calc(100vh-340px)] overflow-auto">
              {blocked.value.map((t) => (
                <TaskRow key={t.id} task={t} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const PRIORITY_RANK: Record<TaskPriority, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

function sortTasks(tasks: TaskItem[]): TaskItem[] {
  return [...tasks].sort((a, b) => {
    const rankDiff = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
    if (rankDiff !== 0) return rankDiff;
    const aTime = a.updated_at ?? a.created_at;
    const bTime = b.updated_at ?? b.created_at;
    return bTime.localeCompare(aTime);
  });
}
